import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import { type CalendarEventStatus, type ScheduleCalendarEvent } from "@fitness-app/data-models/entities/CalendarEvent";
import { type Trainee } from "@fitness-app/data-models/entities/Trainee";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase } from "~/store/initializeStore";

interface UpdateCalendarEventParams {
  eventId: string;
  update: Partial<{
    status: CalendarEventStatus;
    traineeId: string;
    meetingUrl: string | null;
  }>;
}

export const useUpdateCalendarEvent = () => {
  const queryClient = useQueryClient();
  const { userId } = useUserRole();
  const mutation = useMutation(
    async ({ eventId, update }: UpdateCalendarEventParams) => {
      const { error } = await supabase
        .from("calendar_event")
        .update({ ...update, updatedAt: new Date().toISOString() })
        .eq("id", eventId);

      if (error) {
        throw error;
      }

      return { eventId, update };
    },
    {
      onSuccess: (_, { update }) => {
        void message.success("Wydarzenie zostało zaktualizowane");
        if (update.traineeId) {
          queryClient.invalidateQueries(["calendar_events", update.traineeId]);
          queryClient.invalidateQueries(["scheduled_events"]);
        } else {
          queryClient.invalidateQueries(["calendar_events"]);
          queryClient.invalidateQueries(["scheduled_events"]);
        }
      },
      onError: () => {
        void message.error("Nie udało się zaktualizować wydarzenia");
      },
    },
  );

  const linkClient = async (event: ScheduleCalendarEvent, trainee: Trainee) => {
    void mutation.mutate({ eventId: event.id, update: { traineeId: trainee.id } });
    await supabase.from("calendar_event_rule").insert({
      traineeId: trainee.id,
      eventId: event.id,
      originalEmail: event.eventData.email || "",
      validEmail: trainee.email,
      addedBy: userId,
    });
  };

  return { mutation, linkClient };
};
